// DeleteAccount (§11) — Google Play requires an in-app account-deletion path.
// Redesigned to the token shell; the deletion logic (reauth → delete Firestore
// doc → deleteUser → clear local) is preserved.
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../services/firebaseService";
import { deleteUser, EmailAuthProvider, reauthenticateWithCredential } from "firebase/auth";
import { doc, deleteDoc } from "firebase/firestore";
import Screen from "./ui/Screen";

const inputStyle: React.CSSProperties = {
  width: "100%", boxSizing: "border-box", height: 46, padding: "0 14px", borderRadius: 12,
  background: "var(--surface)", border: "1px solid var(--border)", color: "var(--text)", outline: "none",
  fontFamily: "'DM Sans',sans-serif", fontSize: 14,
};
const labelStyle: React.CSSProperties = { display: "block", fontSize: 13, fontWeight: 600, color: "var(--text-2)", marginBottom: 6 };

const DeleteAccount: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleDelete = async () => {
    setError("");
    if (confirmText !== "DELETE") { setError("Type DELETE (all caps) to confirm."); return; }
    if (!email || !password) { setError("Enter your email and password to continue."); return; }
    setLoading(true);
    try {
      const user = auth.currentUser;
      if (!user) { setError("No user is currently signed in."); setLoading(false); return; }
      const credential = EmailAuthProvider.credential(email, password);
      await reauthenticateWithCredential(user, credential);
      await deleteDoc(doc(db, "users", user.uid));
      await deleteUser(user);
      localStorage.clear();
      setSuccess(true);
      setTimeout(() => navigate("/"), 3000);
    } catch (err: any) {
      if (err.code === "auth/wrong-password") setError("Incorrect password. Please try again.");
      else if (err.code === "auth/user-not-found") setError("No account found with this email.");
      else if (err.code === "auth/invalid-email") setError("Invalid email address.");
      else if (err.code === "auth/too-many-requests") setError("Too many attempts. Please wait and try again.");
      else setError("Failed to delete account. Please try again or contact support.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Screen title="Delete Account" showBack hideNav onBack={() => navigate(-1)}>
      <div className="mc-in" style={{ padding: "18px 16px", display: "flex", flexDirection: "column", gap: 16 }}>
        {success ? (
          <div style={{ textAlign: "center", padding: "30px 20px", borderRadius: 20, background: "var(--surface)", border: "1px solid rgba(52,211,153,0.3)" }}>
            <div style={{ width: 64, height: 64, margin: "0 auto", borderRadius: "50%", background: "rgba(52,211,153,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#34D399" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
            </div>
            <div style={{ marginTop: 16, fontFamily: "'Space Grotesk',sans-serif", fontSize: 22, fontWeight: 700 }}>Account Deleted</div>
            <div style={{ marginTop: 8, fontSize: 14, color: "var(--text-2)", lineHeight: 1.5 }}>Your account and all associated data have been permanently deleted.</div>
          </div>
        ) : (
          <>
            <div style={{ padding: "14px 16px", borderRadius: 14, background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.3)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 9, color: "#F87171", fontWeight: 700, fontSize: 14 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M10.3 3.9L1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0zM12 9v4M12 17h.01" /></svg>
                This action cannot be undone
              </div>
              <div style={{ marginTop: 8, fontSize: 13, color: "var(--text-2)", lineHeight: 1.5 }}>All your data — game progress, achievements, and profile — will be permanently deleted.</div>
            </div>
            <div><label style={labelStyle}>Email</label><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" style={inputStyle} /></div>
            <div><label style={labelStyle}>Password</label><input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" style={inputStyle} /></div>
            <div><label style={labelStyle}>Type <span style={{ color: "#F87171", fontWeight: 800 }}>DELETE</span> to confirm</label><input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="DELETE" style={inputStyle} /></div>
            {error && <div style={{ padding: "10px 14px", borderRadius: 12, background: "rgba(248,113,113,0.12)", border: "1px solid rgba(248,113,113,0.35)", color: "#F87171", fontSize: 13, fontWeight: 600 }}>{error}</div>}
            <div style={{ display: "flex", gap: 10 }}>
              <button className="mc-tap" onClick={() => navigate(-1)} style={{ flex: 1, minHeight: 48, border: "1px solid var(--border)", borderRadius: 14, background: "var(--border)", color: "var(--text-2)", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>Cancel</button>
              <button className="mc-tap" onClick={handleDelete} disabled={loading} style={{ flex: 1, minHeight: 48, border: "none", borderRadius: 14, background: "#DC2626", color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", opacity: loading ? 0.7 : 1 }}>{loading ? "Deleting…" : "Delete Account"}</button>
            </div>
            <div style={{ paddingTop: 6 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>What will be deleted:</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 13, color: "var(--text-2)" }}>
                <div>• Your user profile and login credentials</div>
                <div>• All game progress and achievements</div>
                <div>• Leaderboard rankings</div>
                <div>• Battle history and statistics</div>
                <div>• Forum posts and messages</div>
              </div>
            </div>
          </>
        )}
      </div>
    </Screen>
  );
};

export default DeleteAccount;
